import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AuthShell, AuthFooter, UnlockOverlay, EyeToggle } from "@/components/brand";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AuthPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [isSignupMode, setIsSignupMode] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  // Password strength checks
  const checks = {
    length:    password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number:    /[0-9]/.test(password),
  };
  const strength = Object.values(checks).filter(Boolean).length;
  const strengthLabel = ["", "Weak", "Fair", "Good", "Strong"][strength];
  const strengthColor = ["", "#c0392b", "#c47a12", "#1f5f8b", "#17a35f"][strength];

  const redirectByRole = (role) => {
    setUnlocking(true);
    setTimeout(() => {
      if (role === "admin") navigate("/admin");
      else if (role === "city_official") navigate("/city");
      else navigate("/home");
    }, 1600);
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { token, email: userEmail, role } = response.data;
      if (role === "client" && !consentAccepted) {
        toast.error("Please accept the transparency notice to continue");
        setLoading(false);
        return;
      }
      localStorage.setItem("token", token);
      localStorage.setItem("email", userEmail);
      localStorage.setItem("role", role);
      redirectByRole(role);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally { setLoading(false); }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!consentAccepted) { toast.error("Please accept the transparency notice"); return; }
    if (password !== confirmPassword) { toast.error("Passwords do not match"); return; }
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/signup`, {
        email, password, consent_accepted: consentAccepted, role: "client", name: name.trim() || undefined
      });
      const { token, email: userEmail, role } = response.data;
      localStorage.setItem("token", token);
      localStorage.setItem("email", userEmail);
      localStorage.setItem("role", role);
      redirectByRole(role);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Signup failed");
    } finally { setLoading(false); }
  };

  return (
    <>
      {unlocking && <UnlockOverlay label="Unlocking your portal" />}

      <AuthShell
        eyebrow="Cannabis Equity Advisory"
        title={isSignupMode ? "Create your access" : "Access your portal"}
        headerRight={
          <button onClick={() => navigate("/admin-login")}
            className="eyebrow hover:text-ink transition-colors"
            data-testid="admin-login-link">
            Admin Access
          </button>
        }
        after={
          <div className="mt-4">
            {!isSignupMode ? (
              <button type="button"
                onClick={() => { setIsSignupMode(true); setPassword(""); setConfirmPassword(""); }}
                className="btn-ghost w-full h-11 text-equal-dark border-equal/40 hover:bg-mint hover:border-equal">
                Request Access — Create a Free Account
              </button>
            ) : (
              <button type="button"
                onClick={() => { setIsSignupMode(false); setPassword(""); setConfirmPassword(""); }}
                className="btn-ghost w-full h-11">
                Already have an account? Sign in
              </button>
            )}
          </div>
        }
        footer={
          <AuthFooter>
            Equal &copy; 2025 &nbsp;·&nbsp;
            <a href="/privacy" target="_blank" className="hover:text-ink transition-colors">Privacy</a>
            &nbsp;·&nbsp;
            <a href="/terms" target="_blank" className="hover:text-ink transition-colors">Terms</a>
          </AuthFooter>
        }
      >
        {/* Google — primary CTA */}
        <div>
          <p className="text-center text-xs text-mute mb-2 font-medium">Fastest way to get started</p>
          <button
            onClick={() => {
              const redirectUrl = encodeURIComponent(`${window.location.origin}/google-callback`);
              window.location.href = `https://auth.emergentagent.com/?redirect=${redirectUrl}`;
            }}
            className="btn-ghost w-full h-12 text-[15px] font-semibold"
            data-testid="google-signin-button">
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-line" />
          <span className="text-mute text-xs">or sign in with email</span>
          <div className="flex-1 h-px bg-line" />
        </div>

        {/* Form */}
        <form onSubmit={isSignupMode ? handleSignup : handlePasswordLogin} className="space-y-3">
          {isSignupMode && (
            <input type="text" placeholder="Full name" value={name}
              onChange={e => setName(e.target.value)}
              autoComplete="name"
              className="input-flat h-11"
              data-testid="name-input" />
          )}

          <input type="email" placeholder="Email address" value={email}
            onChange={e => setEmail(e.target.value)} required
            autoComplete="email"
            className="input-flat h-11"
            data-testid="email-input" />

          <div>
            <div className="relative">
              <input type={showPassword ? "text" : "password"}
                placeholder={isSignupMode ? "Create a password" : "Password"}
                value={password} onChange={e => setPassword(e.target.value)} required
                autoComplete={isSignupMode ? "new-password" : "current-password"}
                className="input-flat h-11 pr-11"
                data-testid="password-input" />
              <EyeToggle shown={showPassword} onClick={() => setShowPassword(!showPassword)} />
            </div>

            {/* Password strength — only in signup mode */}
            {isSignupMode && password.length > 0 && (
              <div className="mt-2 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="flex-1 flex gap-1">
                    {[1,2,3,4].map(i => (
                      <div key={i} className="h-1 flex-1 rounded-full transition-colors duration-300"
                        style={{ background: i <= strength ? strengthColor : "#e9ebed" }} />
                    ))}
                  </div>
                  <span className="text-xs font-medium transition-colors" style={{ color: strengthColor }}>
                    {strengthLabel}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                  {[
                    { key: "length",    label: "8+ characters" },
                    { key: "uppercase", label: "Uppercase letter" },
                    { key: "lowercase", label: "Lowercase letter" },
                    { key: "number",    label: "One number" },
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center gap-1.5">
                      <svg className="w-3 h-3 flex-shrink-0 transition-colors"
                        style={{ color: checks[key] ? "#17a35f" : "#c9d1d9" }}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
                          d={checks[key] ? "M5 13l4 4L19 7" : "M6 18L18 6M6 6l12 12"} />
                      </svg>
                      <span className="text-xs transition-colors"
                        style={{ color: checks[key] ? "#0f8a5a" : "#5b6b7c" }}>
                        {label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!isSignupMode && (
              <div className="flex justify-end mt-1.5">
                <button type="button" onClick={() => navigate("/forgot-password")}
                  className="text-xs text-equal-dark hover:text-ink transition-colors">
                  Forgot password?
                </button>
              </div>
            )}
          </div>

          {isSignupMode && (
            <div className="relative">
              <input type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm password"
                value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required
                autoComplete="new-password"
                className="input-flat h-11 pr-11"
                data-testid="confirm-password-input" />
              <EyeToggle shown={showConfirmPassword} onClick={() => setShowConfirmPassword(!showConfirmPassword)} />
            </div>
          )}

          {/* Consent */}
          <div className="pt-1 space-y-2">
            <div className="flex items-start gap-2.5" data-testid="consent-checkbox-container">
              <button type="button" onClick={() => setConsentAccepted(!consentAccepted)}
                className="flex-shrink-0 mt-0.5 w-4 h-4 rounded border-2 transition-colors flex items-center justify-center"
                style={{ background: consentAccepted ? "#17a35f" : "white", borderColor: consentAccepted ? "#17a35f" : "#c9d1d9" }}
                data-testid="consent-checkbox">
                {consentAccepted && (
                  <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"/>
                  </svg>
                )}
              </button>
              <p className="text-mute text-xs leading-relaxed">
                I accept the{" "}
                <a href="/terms" target="_blank" className="text-equal-dark hover:underline">Terms</a>
                {" "}and{" "}
                <a href="/privacy" target="_blank" className="text-equal-dark hover:underline">Privacy Policy</a>.
                {" "}Sessions are shared with City Officials for transparency.
              </p>
            </div>

            <button type="button" onClick={() => setShowConsent(!showConsent)}
              className="text-xs text-mute hover:text-ink flex items-center gap-1 transition-colors">
              <svg className={`w-3 h-3 transition-transform ${showConsent ? "rotate-90" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
              </svg>
              Notice of Transparency
            </button>
            {showConsent && (
              <div className="p-3 rounded-[10px] bg-warm border border-[#f3d9b0] text-xs text-[#7a4b0a] leading-relaxed">
                Your sessions are shared with City of Long Beach officials for compliance. Equal AI provides informational guidance only — not legal advice.
              </div>
            )}
          </div>

          <button type="submit" disabled={loading}
            className="btn-primary w-full h-11 mt-1"
            data-testid={isSignupMode ? "signup-button" : "login-button"}>
            {loading ? "Authenticating..." : (isSignupMode ? "Create Account" : "Access Portal")}
          </button>
        </form>
      </AuthShell>
    </>
  );
}
